import { useState } from "react";
import { AmountInput, Button, Dot } from "../../components/ui";
import { big, usd } from "../../lib/num";
import type { Account } from "../../sdk";
import { withdraw } from "../../state/actions";
import { useTradingState } from "../../state/market";
import { Line, MaxChip } from "./parts";
import { collateralToUsdc, minimums, typedUsdc, usdcCents, usdcFig, usdcMin, usdcText } from "./shared";

/** A withdrawal of the bankroll ready to play to the wallet's own HyperCore spot account. */
export function Withdraw({ account }: { account: Account }) {
  const paused = useTradingState().data?.pausedActions.includes("withdrawToCore") ?? false;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<bigint | null>(null);

  // the balance is collateral at 18 decimals; a withdrawal names USDC's 6, floored
  const max = collateralToUsdc(big(account.funds.availableBalance));
  const min = minimums().withdrawal;
  const amount = typedUsdc(text);
  const queued = big(account.queued);

  let reason: string | null = null;
  if (paused) reason = "Withdrawals are paused";
  else if (amount === null || amount === 0n) reason = "Enter an amount";
  else if (amount < min) reason = `Minimum ${usdcMin(min)}`;
  else if (amount > max) reason = "Not enough available";

  const submit = async () => {
    if (reason !== null || amount === null) return;
    setBusy(true);
    try {
      await withdraw(usdcText(amount));
      setSent(amount);
      setText("");
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack-lg">
      <div className="field">
        <div className="field-head">
          <span className="label">Amount</span>
          <span className="pf-avail">
            <span className="muted">Ready to play</span>
            <span className="num">{usdcCents(max)}</span>
            <MaxChip disabled={max === 0n} onClick={() => setText(usdcText(max))} />
          </span>
        </div>
        <AmountInput
          label="Withdraw amount"
          value={text}
          onChange={(next) => {
            setText(next);
            setSent(null);
          }}
          places={6}
          suffix="USDC"
          onEnter={() => void submit()}
        />
        <p className="pf-fine">Minimum {usdcMin(min)}.</p>
      </div>

      <Line label="To">Your HyperCore spot account (same address)</Line>

      {queued > 0n && (
        <p className="note">{usd(queued)} in queued wins can be withdrawn once the payout queue pays it into your bankroll.</p>
      )}

      {sent !== null && (
        <p className="pf-sent" role="status">
          <Dot tone="up" />
          {usdcFig(sent)} USDC sent. It shows under Pending until it settles.
        </p>
      )}

      <Button tone="primary" size="lg" block busy={busy} disabled={reason !== null} onClick={() => void submit()}>
        {reason ?? "Withdraw"}
      </Button>
      <p className="pf-fine">One signature on HyperEVM. No gas.</p>
    </div>
  );
}
